/**
 * 도메인 타입.
 *
 * 설계 원칙 두 가지가 여기에 박혀 있다.
 *
 * 1. 시간은 절대 UTC로 변환해서 저장하지 않는다.
 *    `localTime`은 벽시계 시간('09:00')이고, 타임존은 `TripDay`에 붙는다.
 *    이유: "오전 9시 조식"은 현지 9시여야 한다. UTC로 저장하면 일정을
 *    하루 미루거나 도시를 바꿀 때 시간이 엉뚱하게 이동한다.
 *
 * 2. 순서는 정수 인덱스가 아니라 fractional index 문자열이다.
 *    두 사람이 동시에 순서를 바꿔도 충돌하지 않고, 재정렬할 때
 *    전체 행을 업데이트하지 않아도 된다. (실시간 공동 편집 전제)
 */

export type Region = 'KR' | 'GLOBAL';

export type TransportMode = 'walk' | 'transit' | 'car';

/**
 * 일정 항목의 종류.
 *
 * place를 뺀 나머지는 **구간 항목**이다 — 한 지점에 머무는 게 아니라 터미널
 * 에서 터미널로 이동하는 한 줄이다. 그래서 출발 좌표(coord)와 함께 도착
 * 좌표(toCoord)를 가질 수 있고, 지도에는 점이 아니라 선으로 그려진다.
 *
 * 편·시각은 사용자가 직접 넣는다. 항공·선박 시간표는 무료로 조회할 방법이
 * 마땅치 않고, 기차·광역버스도 예매 사이트를 거쳐야 정확하기 때문이다.
 */
export type ItemKind = 'place' | 'flight' | 'train' | 'bus' | 'ferry';

/** 구간 항목인지 — 터미널에서 터미널로 이동하는 종류인가. */
export function isSegmentKind(kind: ItemKind): boolean {
  return kind !== 'place';
}

export interface Coord {
  lat: number;
  lng: number;
}

export interface Member {
  id: string;
  name: string;
  /** 아바타에 표시할 한 글자 */
  initial: string;
  color: string;
  /** 같은 닉네임끼리 구분하는 4자리 번호. 없을 수 있다(태그 도입 전 DB). */
  tag?: string;
}

/**
 * 멤버 이름표. 같은 여행에 같은 닉네임이 있을 때만 번호를 붙인다.
 * 늘 붙이면 "여행자#0421님이 고침"처럼 읽기 번거롭고, 안 붙이면 둘을 못 가른다.
 */
export function memberLabel(member: Member, members: readonly Member[]): string {
  const clash = members.some((m) => m.id !== member.id && m.name === member.name);
  return clash && member.tag ? `${member.name}#${member.tag}` : member.name;
}

export interface TripDay {
  /** 'YYYY-MM-DD' */
  date: string;
  /** IANA 타임존. day 단위로 부여한다. */
  timezone: string;
  /** '방콕' 같은 그 날의 거점 도시 */
  cityLabel: string;
  /**
   * 그 날 묵는 숙소. 하루의 끝(마지막 일정 → 숙소)이자 다음 날의 시작(숙소 → 첫 일정).
   * lodging.sql 이전 DB에는 칸이 없다.
   */
  lodgingName?: string;
  lodgingCoord?: Coord;
}

/** 앞 항목에서 이 항목까지의 이동 */
export interface Leg {
  mode: TransportMode;
  minutes: number;
  /**
   * 사용자가 직접 고친 값인지.
   * true면 길찾기 API 결과로 덮어쓰지 않는다. — 이걸 안 지키면
   * 현지에서 손으로 적어둔 시간이 새로고침 때마다 날아간다.
   */
  isManual: boolean;
  /** 해당 지역에 대중교통 데이터가 없어 mode를 강제로 바꿨을 때 */
  fellBackFrom?: TransportMode;
}

export interface Item {
  id: string;
  tripId: string;
  /** 'YYYY-MM-DD' — 어느 날짜에 속하는지 */
  date: string;
  /** fractional index. 같은 날짜 안에서 정렬 기준. */
  sortKey: string;
  kind: ItemKind;
  title: string;
  placeName?: string;
  coord?: Coord;
  /** 벽시계 시간 'HH:mm'. 타임존은 이 항목이 속한 TripDay가 가진다. */
  localTime?: string;
  /** 머무는 시간(분) */
  durationMin?: number;
  description?: string;
  /** 앞 항목에서 여기까지의 이동. 그 날의 첫 항목은 없다. */
  leg?: Leg;
  /** kind가 'place'가 아닐 때의 편명·노선명 등 */
  carrierCode?: string;
  /** 항공권·숙소·투어의 예약(확인) 번호. expenses.sql 이전 DB에는 칸이 없다. */
  bookingRef?: string;
  /**
   * 구간 항목의 도착 지점. 출발은 coord가 맡는다.
   *
   * 이게 있어야 지도에 선을 그릴 수 있다. 없으면 출발 터미널만 점으로 찍히고
   * 다음 일정까지 어떻게 갔는지가 지도에서 사라진다.
   */
  toCoord?: Coord;
  toPlaceName?: string;
  /**
   * 마지막으로 고친 사람(멤버 id)과 시각(ISO). 같이 짜는 일정에서 "이거
   * 누가 바꿨어?"에 답한다. DB 트리거가 채우므로 앱이 보내지 않는다.
   */
  updatedBy?: string;
  updatedAt?: string;
}

export interface Trip {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  ownerId: string;
  /** 친구 초대용 코드 */
  inviteCode: string;
  coverEmoji: string;
  members: Member[];
  days: TripDay[];
}

/**
 * 가계부 한 줄 = 한 번 낸 돈. 정산은 저장하지 않고 domain/settle.ts가 계산한다.
 */
export interface Expense {
  id: string;
  tripId: string;
  title: string;
  /** 통화의 보통 단위(원·엔·달러). 0보다 크다. */
  amount: number;
  /** ISO 4217 (KRW, JPY …) */
  currency: string;
  /** 낸 사람. 계정이 지워졌으면 없다. */
  paidBy?: string;
  /** 나눠 낼 사람들(멤버 id). 나간 멤버도 남는다. */
  splitAmong: string[];
  /** 쓴 날 'YYYY-MM-DD' (여행 날짜). 없어도 된다. */
  spentOn?: string;
  createdAt?: string;
  updatedBy?: string;
}

/** 후보 장소 — 날짜를 정하기 전의 "가고 싶은 곳". 일정에 넣으면 지워진다. */
export interface Place {
  id: string;
  tripId: string;
  name: string;
  placeName?: string;
  coord?: Coord;
  note?: string;
  createdBy?: string;
  createdAt?: string;
}

/** 후보 장소에 준 표. 한 사람이 한 장소에 하나. */
export interface PlaceVote {
  placeId: string;
  tripId: string;
  userId: string;
}

/** 일정에 단 댓글. 고치기는 없다(지우고 다시 쓴다). */
export interface Comment {
  id: string;
  tripId: string;
  itemId: string;
  authorId?: string;
  body: string;
  createdAt: string;
}

/**
 * 변경 기록 한 줄 — "민수가 3일차 점심을 고쳤어요". DB 트리거가 쓰고 앱은 읽기만 한다
 * (supabase/activity.sql). 지운 일정은 행이 없으니 제목을 여기 들고 있다.
 */
export interface Activity {
  id: number;
  tripId: string;
  /** 한 사람. 탈퇴했으면 없다. */
  actorId?: string;
  action: 'add' | 'update' | 'delete';
  target: 'item' | 'expense' | 'place';
  targetId?: string;
  title?: string;
  /** 일정의 날짜 / 지출을 쓴 날 'YYYY-MM-DD' */
  date?: string;
  createdAt: string;
}

export interface ChecklistItem {
  id: string;
  tripId: string;
  title: string;
  checked: boolean;
  assigneeId?: string;
}

/*
 * 이동 수단·일정 종류·편명 칸의 이름은 언어마다 달라 i18n/messages로 옮겼다
 * (t.transport · t.kind · t.carrierLabel · t.carrierPlaceholder).
 */
